import { useState } from "react";
import { toast } from "react-toastify";
import Input from "../../../componentes/input/input";
import Button from "../../../componentes/button/button";
import ContasAPagarService from "../../../services/resource/contasAPagarService";
import { formatarData, formatarMoeda } from "../../../utils/solicitacoes/formatters";
import LancamentoFortesCard from "./LancamentoFortesCard";

const service = new ContasAPagarService();

const AVISO_STATUS = {
    PROVAVEL: "Correspondência só pelo valor — confira se é o lançamento certo antes de usar.",
    NAO_ENCONTRADA: null,
    SEM_FORNECEDOR: null,
    FORTES_INDISPONIVEL: null,
};

/**
 * Campo "Código de Lançamento do Fortes" (opcional) das etapas de lançamento de Despesa e Compras:
 * preenchimento manual ou pelo botão "Sincronizar com Fortes", que procura o lançamento correspondente
 * com a mesma conciliação da página Contas a Pagar. Um único lançamento confiável é preenchido sozinho;
 * nos demais casos os candidatos aparecem para o usuário escolher. O código só é gravado com a ação da
 * etapa (o usuário confere antes).
 */
export default function CodigoLancamentoFortesCampo({ solicitacaoId, valor, onChange, disabled = false }) {
    const [sincronizando, setSincronizando] = useState(false);
    const [resultado, setResultado] = useState(null);
    const [expandido, setExpandido] = useState(null);

    function sincronizar() {
        setSincronizando(true);
        setResultado(null);
        service.sincronizarCodigoLancamento(solicitacaoId)
            .then(res => {
                const r = res.data;
                setResultado(r);
                if (r.codigoSugerido) {
                    onChange(r.codigoSugerido);
                    toast.success(`Lançamento ${r.codigoSugerido} encontrado no Fortes (${r.criterio}). Confira antes de concluir a etapa.`);
                } else if (r.candidatos?.length > 0) {
                    toast.info("Mais de uma possibilidade no Fortes — escolha o lançamento correto abaixo.");
                } else if (r.status === "FORTES_INDISPONIVEL") {
                    toast.error(r.criterio);
                } else {
                    toast.warn(`Nenhum lançamento correspondente no Fortes: ${r.criterio}.`);
                }
            })
            .catch(e => toast.error(e?.response?.data?.message || "Não foi possível sincronizar com o Fortes."))
            .finally(() => setSincronizando(false));
    }

    function usar(codigo) {
        onChange(codigo);
        toast.success(`Código ${codigo} preenchido. Confira antes de concluir a etapa.`);
    }

    const candidatos = resultado?.candidatos || [];
    const mostrarCandidatos = candidatos.length > 0 && !resultado?.codigoSugerido;

    return (
        <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-end gap-2">
                <div className="w-full sm:w-72">
                    <Input
                        label="Código de Lançamento do Fortes"
                        value={valor || ""}
                        onChange={e => onChange(e.target.value)}
                        placeholder="Opcional — código gerado pelo Fortes"
                        disabled={disabled}
                    />
                </div>
                <Button
                    variant="outline"
                    label="Sincronizar com Fortes"
                    iconLeft="fa fa-rotate"
                    loading={sincronizando}
                    disabled={disabled || sincronizando}
                    onClick={sincronizar}
                />
            </div>

            {resultado && !mostrarCandidatos && !resultado.codigoSugerido && (
                <p className={`text-xs ${resultado.status === "FORTES_INDISPONIVEL" ? "text-destructive" : "text-muted-foreground"}`}>
                    <i className="fa fa-circle-info mr-1" />
                    {resultado.status === "FORTES_INDISPONIVEL"
                        ? resultado.criterio
                        : `Nenhum lançamento correspondente no Fortes: ${resultado.criterio}. Informe o código manualmente, se tiver.`}
                </p>
            )}

            {resultado?.codigoSugerido && (
                <p className="text-xs text-green-700">
                    <i className="fa fa-check mr-1" />
                    Encontrado pelo critério: {resultado.criterio}.
                </p>
            )}

            {mostrarCandidatos && (
                <div className="flex flex-col gap-2 rounded-lg border border-border p-3">
                    <p className="text-xs text-muted-foreground">
                        Critério: <strong>{resultado.criterio}</strong>.
                        {AVISO_STATUS[resultado.status] && <span className="block text-amber-700">{AVISO_STATUS[resultado.status]}</span>}
                        Escolha o lançamento desta solicitação:
                    </p>
                    {candidatos.map(({ lancamento: l, vinculadoASolicitacao }) => (
                        <div key={l.codigo} className="rounded-md border border-border">
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2 text-sm">
                                <span className="font-semibold">Código {l.codigo}</span>
                                <span>Doc. {l.numeroDocumento || "-"}</span>
                                <span>{formatarMoeda(l.valor)}</span>
                                <span className="text-muted-foreground">Emissão {formatarData(l.dataEmissao)}</span>
                                <span className="text-muted-foreground">{l.filialNome || "-"}</span>
                                <div className="ml-auto flex items-center gap-2">
                                    <button
                                        type="button"
                                        className="text-xs text-primary hover:underline"
                                        onClick={() => setExpandido(expandido === l.codigo ? null : l.codigo)}
                                    >
                                        {expandido === l.codigo ? "Ocultar detalhes" : "Ver detalhes"}
                                    </button>
                                    {vinculadoASolicitacao ? (
                                        <span className="text-xs text-amber-700" title="Este lançamento já está vinculado a outra solicitação">
                                            <i className="fa fa-link mr-1" />Já vinculado à solicitação {vinculadoASolicitacao}
                                        </span>
                                    ) : (
                                        <Button
                                            variant={valor === l.codigo ? "green" : "outline"}
                                            label={valor === l.codigo ? "Selecionado" : "Usar este código"}
                                            iconLeft={valor === l.codigo ? "fa fa-check" : "fa fa-arrow-up"}
                                            disabled={disabled}
                                            onClick={() => usar(l.codigo)}
                                        />
                                    )}
                                </div>
                            </div>
                            {expandido === l.codigo && (
                                <div className="border-t border-border p-3 bg-muted/20"><LancamentoFortesCard lancamento={l} /></div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
